import { convertRestToSectionAtCursor } from '@/components/tiptap/shared/convert-rest-to-section';
import { Button } from '@heidi/design-system/button';
import { TypographyP2 } from '@heidi/design-system/typography';
import { insertDraftSectionAt } from '@heidi/editor/core';
import type { Editor } from '@tiptap/react';
import { Plus } from 'lucide-react';
import { FormattedMessage } from 'react-intl';

interface AddSectionButtonProps {
  editor: Editor;
  /**
   * Reports `section_created` for this click. Injected because the two editor
   * kits stamp different `editor_type` values (and resolve `editor_context`
   * differently) — the button must not hardcode either.
   */
  onSectionCreated: () => void;
}

export function AddSectionButton({ editor, onSectionCreated }: AddSectionButtonProps) {
  const handleClick = () => {
    // Convert content from the exact caret position up to the next section into
    // a new section. Falls back to appending an empty draft section when the
    // cursor can't map cleanly (e.g. it sits on a section heading), so the
    // button is never a dead click.
    const converted = convertRestToSectionAtCursor(editor);
    if (!converted) {
      insertDraftSectionAt(editor, editor.state.doc.content.size);
      editor.commands.scrollIntoView();
    }
    onSectionCreated();
  };

  return (
    <div data-testid="add-section-button-wrapper">
      <Button
        type="button"
        variant="outline"
        size="xs"
        onClick={handleClick}
        data-testid="add-section-button"
      >
        <Plus />
        <TypographyP2>
          <FormattedMessage defaultMessage="Add section" id="XPrlWcxgX+" />
        </TypographyP2>
      </Button>
    </div>
  );
}
